import { FullScreenProgress } from '@/common/components'
import { config as appConfig } from '@/core'
import { ErrorPageType } from '@/core/errors'
import { History, HistoryContext, LocationContext } from '@/core/history'
import { RouteResponse, RouterContext, resolveRoute } from '@/core/router'
import type { CustomError, TenantData } from '@/core/types'
import { TenantMiddleware, UserMiddleware } from '@/features/auth'
import { persistor } from '@/store'
import theme from '@/theme'
import * as amplitude from '@amplitude/analytics-browser'

import { getAmplitudeApiKey } from '@/core/utils'
import { CssBaseline, PaletteMode } from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { Action, Update } from 'history'
import * as React from 'react'
import { PersistGate } from 'redux-persist/integration/react'
import { ErrorPage } from './ErrorPage'

type AppProps = {
  history: History
  tenant: TenantData | CustomError
}

const isCustomError = (tenant: TenantData | CustomError): tenant is CustomError => 'status' in tenant

const toErrorPageError = (error: CustomError): ErrorPageType =>
  Object.assign(new Error(error.message ?? error.statusText), { status: error.status }) as ErrorPageType

export class App extends React.Component<AppProps> {
  state = {
    cxt: undefined as RouterContext | undefined,
    route: undefined as RouteResponse | undefined,
    location: this.props.history.location,
    error: undefined as ErrorPageType | undefined,
    theme: (window?.localStorage?.getItem('theme') === 'dark' ? 'dark' : 'light') as PaletteMode,
  }

  rootUrl = new URL(document?.URL) as unknown as string

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error }
  }

  dispose?: () => void

  async componentDidMount(): Promise<void> {
    const { history } = this.props

    this.dispose = history.listen(this.renderPath)
    this.renderPath({ location: history.location, action: Action.Pop })

    if (!amplitude.getDeviceId()) {
      const apiKey = getAmplitudeApiKey() || ''
      amplitude.init(apiKey, {
        defaultTracking: true,
        serverZone: 'EU',
      })
    }
  }

  componentDidUpdate(): void {
    if (this.state.route?.title) {
      self.document.title = `${this.state.route.title} - ${appConfig.app.name}`
    }
  }

  componentWillUnmount(): void {
    if (this.dispose) this.dispose()
  }

  componentDidCatch(error: Error, errorInfo: unknown): void {
    if (error.name === 'ChunkLoadError') {
      const refreshed = sessionStorage.getItem('retry-lazy-refreshed')

      if (refreshed !== 'true') {
        sessionStorage.setItem('retry-lazy-refreshed', 'true')
        window.location.reload()
        return
      }
    }

    // You can also log the error to an error reporting service
    console.error(error, errorInfo)
  }

  renderPath = async (ctx: Update): Promise<void> => {
    const { tenant } = this.props
    if (isCustomError(tenant)) {
      this.setState({ error: toErrorPageError(tenant) })
      return
    }

    if (!tenant.id) {
      this.setState({ error: Object.assign(new Error('Tenant not found.'), { status: 404 }) as ErrorPageType })
      return
    }

    // A tab click only moves the search, and `resolveRoute` is keyed on the pathname alone, so
    // it would answer with the route already showing. Awaiting it redraws a turn late, which
    // leaves a controlled tab bar rendering the previous tab - and Base UI reads a click on the
    // tab it still believes is open as no change at all, so a second quick click is dropped.
    if (this.state.route && ctx.location.pathname === this.state.location.pathname) {
      // Carries the route's own error rather than clearing outright, so a path that failed to
      // resolve keeps its error page while one a rendered tab threw is let go of - the same two
      // outcomes the resolve path below produces for this route.
      this.setState({ location: ctx.location, error: this.state.route.error })
      return
    }

    resolveRoute({
      path: ctx.location.pathname,
    }).then((route) => {
      if (route.error) console.error(route.error)
      this.setState({ route, location: ctx.location, error: route.error })
    })
  }

  handleChangeTheme = (): void => {
    this.setState((x: { theme: PaletteMode }) => {
      const theme = x.theme === 'light' ? 'dark' : 'light'
      window.localStorage?.setItem('theme', theme)
      return { ...x, theme }
    })
  }

  render(): JSX.Element {
    const { history, tenant } = this.props
    const { route, location, error } = this.state
    const errorToRender = error ?? (isCustomError(tenant) ? toErrorPageError(tenant) : undefined)

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme[this.state.theme]}>
          {errorToRender ? (
            <ErrorPage error={errorToRender} history={history} />
          ) : (
            <HistoryContext.Provider value={history}>
              <LocationContext.Provider value={location}>
                <PersistGate loading={<FullScreenProgress />} persistor={persistor}>
                  <CssBaseline />
                  <TenantMiddleware tenant={isCustomError(tenant) ? null : tenant}>
                    <UserMiddleware handleChangeTheme={this.handleChangeTheme} route={route} />
                  </TenantMiddleware>
                </PersistGate>
              </LocationContext.Provider>
            </HistoryContext.Provider>
          )}
        </ThemeProvider>
      </StyledEngineProvider>
    )
  }
}
